import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Trophy,
  Medal,
  Award,
  MessageSquare,
  FileText,
  Star,
  Loader,
} from "lucide-react";
import communityService from "../../../services/communityService";

const LeaderboardTable = ({ communityId }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    if (communityId) {
      loadLeaderboard();
    }
  }, [communityId, period]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await communityService.getLeaderboard(
        communityId,
        period,
        10
      );
      if (response.success) {
        setLeaderboard(response.data?.leaderboard || []);
      }
    } catch (error) {
toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1:
        return {
          icon: <Trophy size={20} />,
          color: "#FFD700",
          label: "1st",
        };
      case 2:
        return {
          icon: <Medal size={20} />,
          color: "#C0C0C0",
          label: "2nd",
        };
      case 3:
        return {
          icon: <Award size={20} />,
          color: "#CD7F32",
          label: "3rd",
        };
      default:
        return {
          icon: null,
          color: "#9e9e9e",
          label: `${rank}th`,
        };
    }
  };

  if (loading) {
    return (
      <div className="leaderboard-loading">
        <Loader className="spinner" size={32} />
        <p>Loading leaderboard...</p>
      </div>
    );
  }

  return (
    <div className="leaderboard-table">
      <div className="leaderboard-header">
        <h3>
          <Trophy size={20} />
          Community Leaderboard
        </h3>
        <div className="leaderboard-period-tabs">
          <button
            className={`period-tab ${period === "week" ? "active" : ""}`}
            onClick={() => setPeriod("week")}
          >
            Week
          </button>
          <button
            className={`period-tab ${period === "month" ? "active" : ""}`}
            onClick={() => setPeriod("month")}
          >
            Month
          </button>
          <button
            className={`period-tab ${period === "all" ? "active" : ""}`}
            onClick={() => setPeriod("all")}
          >
            All Time
          </button>
        </div>
      </div>

      {leaderboard.length === 0 ? (
        <div className="leaderboard-empty">
          <Trophy size={48} />
          <p>No leaderboard data available yet</p>
        </div>
      ) : (
        <div className="leaderboard-list">
          <div className="leaderboard-table-header">
            <div className="header-rank">Rank</div>
            <div className="header-user">User</div>
            <div className="header-stats">Threads</div>
            <div className="header-stats">Replies</div>
            <div className="header-stats">Upvotes</div>
            <div className="header-stats">Awards</div>
          </div>

          {leaderboard.map((entry) => {
            const rankBadge = getRankBadge(entry.rank);
            // API returns: { rank, user: { name, profilePhotoUrl, _id }, stats: { threadsCreated, repliesPosted, totalUpvotes, totalReactions } }
            const user = entry.user || {};
            const stats = entry.stats || {};
            return (
              <div
                key={user._id || entry._id || entry.rank}
                className={`leaderboard-row rank-${entry.rank}`}
                style={{
                  backgroundColor:
                    entry.rank <= 3 ? `${rankBadge.color}10` : "transparent",
                }}
              >
                <div
                  className="row-rank"
                  style={{
                    color: rankBadge.color,
                  }}
                >
                  {rankBadge.icon}
                  <span>{rankBadge.label}</span>
                </div>
                <div className="row-user">
                  <div className="user-avatar">
                    {user.profilePhotoUrl ? (
                      <img src={user.profilePhotoUrl} alt={user.name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {user.name?.charAt(0).toUpperCase() || "?"}
                      </div>
                    )}
                  </div>
                  <span>{user.name || user.username || "Unknown"}</span>
                </div>
                <div className="row-stat">
                  <FileText size={16} />
                  {stats.threadsCreated || 0}
                </div>
                <div className="row-stat">
                  <MessageSquare size={16} />
                  {stats.repliesPosted || 0}
                </div>
                <div className="row-stat">
                  <Star size={16} />
                  {stats.totalUpvotes || 0}
                </div>
                <div className="row-stat">
                  <Award size={16} />
                  {stats.totalReactions || 0}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeaderboardTable;
